import { useEffect, useRef } from 'react'

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  opacity: number
}

const PARTICLE_COUNT = 60
const CONNECTION_DISTANCE = 100
const MAX_CONNECTIONS = 3

export default function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined

    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined

    let width: number
    let height: number
    let particles: Particle[] = []
    let animId: number
    let isVisible = true

    function resize() {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      width = canvas.width
      height = canvas.height
    }

    function handleVisibility() {
      if (document.hidden) {
        isVisible = false
        cancelAnimationFrame(animId)
        return undefined
      }
      isVisible = true
      animate()
      return undefined
    }

    function initParticles() {
      particles = []
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 1.5 + 0.5,
          opacity: Math.random() * 0.5 + 0.2,
        })
      }
    }

    function drawConnections() {
      for (let i = 0; i < particles.length; i++) {
        let connections = 0
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < CONNECTION_DISTANCE && connections < MAX_CONNECTIONS) {
            const opacity = (1 - dist / CONNECTION_DISTANCE) * 0.15
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.strokeStyle = `rgba(0, 229, 89, ${opacity})`
            ctx.lineWidth = 0.5
            ctx.stroke()
            connections++
          }
        }
      }
    }

    function animate() {
      if (!isVisible) return

      ctx.clearRect(0, 0, width, height)

      particles.forEach((p) => {
        // eslint-disable-next-line no-param-reassign
        p.x += p.vx
        // eslint-disable-next-line no-param-reassign
        p.y += p.vy

        // eslint-disable-next-line no-param-reassign
        if (p.x < 0 || p.x > width) p.vx *= -1
        // eslint-disable-next-line no-param-reassign
        if (p.y < 0 || p.y > height) p.vy *= -1

        ctx.beginPath()
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(0, 229, 89, ${p.opacity})`
        ctx.fill()
      })

      drawConnections()
      animId = requestAnimationFrame(animate)
    }

    resize()
    initParticles()
    animate()

    window.addEventListener('resize', resize)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('resize', resize)
      document.removeEventListener('visibilitychange', handleVisibility)
      cancelAnimationFrame(animId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  )
}
